import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';
import { formatDateTime } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { usePagination } from '@/hooks/usePagination';

// Maps Notion "Estado" values (Spanish) to Tailwind badge colors
function getStatusColor(status: string): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'abierto' || s === 'pendiente' || s === 'open' || s === 'por hacer') return 'bg-blue-500';
  if (s === 'en curso') return 'bg-yellow-500';
  if (s === 'esperando aprobación' || s === 'en espera (prioridades)' || s === 'en espera') return 'bg-orange-500';
  if (s === 'pendiente por tech' || s === 'pendiente tercept' || s === 'pendiente por el reporter') return 'bg-amber-400';
  if (s === 'en pruebas') return 'bg-purple-500';
  if (s === 'completado' || s === 'resuelto' || s === 'resolved' || s === 'cerrado') return 'bg-green-500';
  if (s === 'cancelled' || s === 'no se corregirá') return 'bg-gray-400';
  return 'bg-gray-500';
}

// Tickets del Kanban "Pedidos usuarios" no van a Notion: su estado es la columna
const KANBAN_LABELS: Record<string, string> = {
  todo: 'Por hacer',
  in_progress: 'En curso',
  waiting: 'En espera',
  done: 'Cerrado',
};

const TERMINAL = new Set(['CANCELLED', 'RESOLVED', 'COMPLETED', 'WONT_FIX']);

export function MyTicketsPage() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [confirmTicketId, setConfirmTicketId] = useState<string | null>(null);

  const { data, isLoading, error } = trpc.tickets.getMyTickets.useQuery();

  const cancelMutation = trpc.tickets.cancelTicket.useMutation({
    onSuccess: () => {
      utils.tickets.getMyTickets.invalidate();
      setConfirmTicketId(null);
    },
  });

  const tickets = data?.data;
  const ticketToCancel = tickets?.find((ticket) => ticket.ticketId === confirmTicketId);
  // Paginado: el historial de tickets de una persona crece sin tope (hook antes de los returns tempranos)
  const { pageItems: pageTickets, footer: paginationFooter } = usePagination(tickets ?? [], 25);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">{t('tickets.page.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('errors.generic', { message: error.message })}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-bold tracking-tight mb-6">{t('tickets.page.title')}</h2>
      <Card>
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tickets.page.table.id')}</TableHead>
              <TableHead>{t('tickets.page.table.title')}</TableHead>
              <TableHead>{t('tickets.page.table.type')}</TableHead>
              <TableHead>{t('tickets.page.table.priority')}</TableHead>
              <TableHead>{t('tickets.page.table.status')}</TableHead>
              <TableHead>{t('tickets.page.table.date')}</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageTickets.map((ticket) => {
              const displayStatus = ticket.kanbanEstado
                ? (KANBAN_LABELS[ticket.kanbanEstado] ?? ticket.kanbanEstado)
                : (ticket.notionEstado ?? ticket.status);
              const isTerminal = TERMINAL.has(ticket.status);
              return (
                <TableRow key={ticket.ticketId}>
                  <TableCell className="font-mono text-xs">{ticket.ticketId.slice(0, 8)}</TableCell>
                  <TableCell className="font-medium">{ticket.title}</TableCell>
                  <TableCell>{ticket.type}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{ticket.priority}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(displayStatus)}>{displayStatus}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(ticket.createdAt)}
                  </TableCell>
                  <TableCell>
                    {!isTerminal && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => setConfirmTicketId(ticket.ticketId)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
        {paginationFooter && <div className="px-4 pb-3">{paginationFooter}</div>}
      </Card>

      <AlertDialog
        open={!!confirmTicketId}
        onOpenChange={(open) => !open && setConfirmTicketId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('tickets.close_dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {ticketToCancel && (
                <><strong>{ticketToCancel.title}</strong><br /></>
              )}
              {t('tickets.close_dialog.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('tickets.close_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmTicketId && cancelMutation.mutate({ ticketId: confirmTicketId })}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? t('tickets.close_dialog.confirming') : t('tickets.close_dialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
